import { useState, useEffect, useRef, useCallback } from 'react';
import { sendVerificationCode, resetPassword } from '@/api/auth';
import { ApiRequestError } from '@/api/types';

// ─── 密码强度 ──────────────────────────────────────────────────────────────────
function getPasswordStrength(pwd: string): { score: number; label: string; color: string } {
  if (!pwd) return { score: 0, label: '', color: '' };
  let score = 0;
  if (pwd.length >= 8) score++;
  if (pwd.length >= 12) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  if (score <= 1) return { score, label: '弱', color: '#ef4444' };
  if (score <= 3) return { score, label: '中', color: '#f97316' };
  return { score, label: '强', color: '#10b981' };
}

// ─── 主组件 ────────────────────────────────────────────────────────────────────
type Step = 'email' | 'verify' | 'reset' | 'done';

interface Props {
  onClose: () => void;
}

export default function ForgotPasswordModal({ onClose }: Props) {
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sending, setSending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [codeError, setCodeError] = useState('');
  const codeRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [resetting, setResetting] = useState(false);

  // 倒计时
  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  // 发送验证码
  const handleSendCode = useCallback(async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('请输入正确的邮箱地址');
      return;
    }
    setEmailError('');
    setSending(true);
    try {
      await sendVerificationCode({ email, purpose: 'reset' });
      setCountdown(60);
      setStep('verify');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : '发送失败，请稍后重试';
      setEmailError(msg);
    } finally {
      setSending(false);
    }
  }, [email]);

  // 6 位验证码输入
  const handleCodeInput = (val: string, idx: number) => {
    const cleaned = val.replace(/\D/g, '').slice(-1);
    const next = [...code];
    next[idx] = cleaned;
    setCode(next);
    setCodeError('');
    if (cleaned && idx < 5) codeRefs.current[idx + 1]?.focus();
  };

  const handleCodeKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === 'Backspace' && !code[idx] && idx > 0) {
      codeRefs.current[idx - 1]?.focus();
    }
  };

  const handleCodePaste = (e: React.ClipboardEvent) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setCode(pasted.split(''));
      codeRefs.current[5]?.focus();
    }
  };

  // 验证码确认（纯前端格式校验，真正验证在 reset-password 接口里做）
  const handleVerify = () => {
    const entered = code.join('');
    if (entered.length < 6) {
      setCodeError('请输入完整的 6 位验证码');
      return;
    }
    setCodeError('');
    setStep('reset');
  };

  // 重置密码
  const handleReset = async () => {
    if (newPwd.length < 8) { setPwdError('密码至少 8 位'); return; }
    if (!/[A-Za-z]/.test(newPwd) || !/[0-9]/.test(newPwd)) { setPwdError('密码需包含字母和数字'); return; }
    if (newPwd !== confirmPwd) { setPwdError('两次密码不一致'); return; }
    setPwdError('');
    setResetting(true);
    try {
      await resetPassword({ email, code: code.join(''), newPassword: newPwd });
      setStep('done');
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : '重置失败，请稍后重试';
      // 验证码错误时退回验证步骤
      if (err instanceof ApiRequestError && (err.httpStatus === 400)) {
        setCode(['', '', '', '', '', '']);
        setStep('verify');
        setCodeError(msg);
      } else {
        setPwdError(msg);
      }
    } finally {
      setResetting(false);
    }
  };

  const pwdStrength = getPasswordStrength(newPwd);

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[300] p-4"
    >
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl overflow-hidden">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            {step !== 'email' && step !== 'done' && (
              <button
                onClick={() => {
                  if (step === 'verify') setStep('email');
                  if (step === 'reset') setStep('verify');
                }}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-line text-gray-500"></i>
              </button>
            )}
            <div>
              <h3 className="font-bold text-gray-900 text-base">找回密码</h3>
              <div className="flex items-center gap-1.5 mt-0.5">
                {(['email', 'verify', 'reset'] as const).map((s, i) => (
                  <div key={s} className={`flex items-center gap-1.5 ${i < 2 ? 'contents' : ''}`}>
                    <div className={`w-2 h-2 rounded-full transition-colors ${
                      step === 'done' || ['email', 'verify', 'reset'].indexOf(step) >= i
                        ? 'bg-[#0ea5e9]' : 'bg-gray-200'
                    }`}></div>
                    {i < 2 && <div className={`w-6 h-px ${
                      step === 'done' || ['email', 'verify', 'reset'].indexOf(step) > i
                        ? 'bg-[#0ea5e9]' : 'bg-gray-200'
                    }`}></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-gray-500"></i>
          </button>
        </div>

        <div className="p-6">
          {/* ── Step 1: 输入邮箱 ── */}
          {step === 'email' && (
            <div>
              <div className="text-center mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-[#0ea5e9]/10 rounded-2xl mx-auto mb-3">
                  <i className="ri-mail-send-line text-[#0ea5e9] text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">输入注册邮箱</h4>
                <p className="text-sm text-gray-400">我们将向您的邮箱发送验证码</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">邮箱地址</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setEmailError(''); }}
                    onKeyDown={(e) => e.key === 'Enter' && handleSendCode()}
                    placeholder="请输入注册时的邮箱"
                    className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none transition-colors ${
                      emailError ? 'border-red-300 focus:border-red-400' : 'border-gray-200 focus:border-[#0ea5e9]'
                    }`}
                  />
                  {emailError && (
                    <p className="mt-1.5 text-xs text-red-500 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>{emailError}
                    </p>
                  )}
                </div>
                <button
                  onClick={handleSendCode}
                  disabled={sending}
                  className="w-full py-3 bg-[#0ea5e9] text-white font-semibold rounded-xl hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {sending ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>发送中...</>
                  ) : (
                    <><i className="ri-send-plane-line"></i>发送验证码</>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* ── Step 2: 验证码 ── */}
          {step === 'verify' && (
            <div>
              <div className="text-center mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-[#10b981]/10 rounded-2xl mx-auto mb-3">
                  <i className="ri-shield-keyhole-line text-[#10b981] text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">输入验证码</h4>
                <p className="text-sm text-gray-400">
                  验证码已发送至 <span className="text-gray-700 font-medium">{email}</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">验证码有效期 10 分钟</p>
              </div>

              <div className="flex gap-2 justify-center mb-2" onPaste={handleCodePaste}>
                {code.map((digit, idx) => (
                  <input
                    key={idx}
                    ref={(el) => { codeRefs.current[idx] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeInput(e.target.value, idx)}
                    onKeyDown={(e) => handleCodeKeyDown(e, idx)}
                    className={`w-11 h-12 text-center text-lg font-bold border-2 rounded-xl focus:outline-none transition-colors ${
                      codeError
                        ? 'border-red-300 text-red-500'
                        : digit
                        ? 'border-[#0ea5e9] text-[#0ea5e9]'
                        : 'border-gray-200 focus:border-[#0ea5e9]'
                    }`}
                  />
                ))}
              </div>

              {codeError && (
                <p className="text-center text-xs text-red-500 mb-3 flex items-center justify-center gap-1">
                  <i className="ri-error-warning-line"></i>{codeError}
                </p>
              )}

              <div className="text-center mb-5">
                {countdown > 0 ? (
                  <span className="text-sm text-gray-400">{countdown}s 后可重新发送</span>
                ) : (
                  <button
                    onClick={handleSendCode}
                    disabled={sending}
                    className="text-sm text-[#0ea5e9] hover:text-[#0284c7] cursor-pointer disabled:opacity-60"
                  >
                    {sending ? '发送中...' : '重新发送验证码'}
                  </button>
                )}
              </div>

              <button
                onClick={handleVerify}
                className="w-full py-3 bg-[#0ea5e9] text-white font-semibold rounded-xl hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap"
              >
                下一步
              </button>
            </div>
          )}

          {/* ── Step 3: 设置新密码 ── */}
          {step === 'reset' && (
            <div>
              <div className="text-center mb-6">
                <div className="w-14 h-14 flex items-center justify-center bg-[#f97316]/10 rounded-2xl mx-auto mb-3">
                  <i className="ri-lock-password-line text-[#f97316] text-2xl"></i>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">设置新密码</h4>
                <p className="text-sm text-gray-400">请设置一个安全的新密码</p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">新密码</label>
                  <div className="relative">
                    <input
                      type={showNewPwd ? 'text' : 'password'}
                      value={newPwd}
                      onChange={(e) => { setNewPwd(e.target.value); setPwdError(''); }}
                      placeholder="至少 8 位，包含字母和数字"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9] transition-colors pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={`${showNewPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                    </button>
                  </div>
                  {newPwd && (
                    <div className="mt-2">
                      <div className="flex gap-1 mb-1">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <div
                            key={i}
                            className="flex-1 h-1 rounded-full transition-all duration-300"
                            style={{ backgroundColor: i <= pwdStrength.score ? pwdStrength.color : '#e5e7eb' }}
                          ></div>
                        ))}
                      </div>
                      <p className="text-xs" style={{ color: pwdStrength.color }}>密码强度：{pwdStrength.label}</p>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">确认新密码</label>
                  <div className="relative">
                    <input
                      type={showConfirmPwd ? 'text' : 'password'}
                      value={confirmPwd}
                      onChange={(e) => { setConfirmPwd(e.target.value); setPwdError(''); }}
                      placeholder="再次输入新密码"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#0ea5e9] transition-colors pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={`${showConfirmPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                    </button>
                  </div>
                </div>
                {pwdError && (
                  <p className="text-sm text-red-500 flex items-center gap-1">
                    <i className="ri-error-warning-line"></i>{pwdError}
                  </p>
                )}
                <button
                  onClick={handleReset}
                  disabled={resetting}
                  className="w-full py-3 bg-[#0ea5e9] text-white font-semibold rounded-xl hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap disabled:opacity-70 flex items-center justify-center gap-2"
                >
                  {resetting ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>重置中...</>
                  ) : '确认重置密码'}
                </button>
              </div>
            </div>
          )}

          {/* ── Step 4: 完成 ── */}
          {step === 'done' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 flex items-center justify-center bg-[#10b981]/10 rounded-full mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-[#10b981] text-3xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 text-lg mb-2">密码重置成功！</h4>
              <p className="text-sm text-gray-400 mb-6">您的密码已成功更新，请使用新密码登录。</p>
              <button
                onClick={onClose}
                className="w-full py-3 bg-[#0ea5e9] text-white font-semibold rounded-xl hover:bg-[#0284c7] transition-all cursor-pointer whitespace-nowrap"
              >
                去登录
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
